import { useEffect } from 'react';

/**
 * Update document.title and common <meta> tags on the fly per route.
 * Restores nothing on unmount — the next page's hook overwrites values.
 *
 * Usage:
 *   useDocumentMeta({
 *     title: 'Exaustores ITC | ELETROVEX',
 *     description: '...',
 *     canonical: 'https://eletrovex.com.br/categoria/exaustores',
 *     image: 'https://...',
 *     type: 'product' | 'website',
 *     jsonLd: { ... }        // optional: single object or array; injected as <script>
 *   });
 */
const upsertMeta = (attrKey, attrVal, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attrKey}="${attrVal}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attrKey, attrVal);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const upsertLink = (rel, href) => {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const JSONLD_ID = 'app-jsonld';
const setJsonLd = (data) => {
  const existing = document.getElementById(JSONLD_ID);
  if (existing) existing.remove();
  if (!data) return;
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = JSONLD_ID;
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
};

export const useDocumentMeta = ({ title, description, canonical, image, type = 'website', jsonLd, robots = 'index, follow, max-image-preview:large' } = {}) => {
  useEffect(() => {
    if (title) document.title = title;
    upsertMeta('name', 'robots', robots);
    if (description) {
      upsertMeta('name', 'description', description);
      upsertMeta('property', 'og:description', description);
      upsertMeta('name', 'twitter:description', description);
    }
    if (title) {
      upsertMeta('property', 'og:title', title);
      upsertMeta('name', 'twitter:title', title);
    }
    upsertMeta('property', 'og:type', type);
    if (image) {
      upsertMeta('property', 'og:image', image);
      upsertMeta('name', 'twitter:image', image);
      upsertMeta('name', 'twitter:card', 'summary_large_image');
    }
    if (canonical) {
      upsertMeta('property', 'og:url', canonical);
      upsertLink('canonical', canonical);
    }
    setJsonLd(jsonLd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, canonical, image, type, robots, JSON.stringify(jsonLd || null)]);
};

export default useDocumentMeta;
